import { api, apiWithRetry, unwrapApiResult } from './api'
import {
  OrderInterface,
  OrderResponseInterface,
  OrderType,
  OrderTypeSummaryItem,
  OrderTypeSummaryResponse,
  OrderValidationResponse,
  parseOrderModel,
  parseOrderTypeSummary,
  parseOrderValidationModel,
} from '../models/OrderModel'
import { ItemInterface, parseItemModel } from '../models/ItemModel'
import {
  parseSettingsModel,
  SettingsInterface,
  SettingsResponseInterface,
} from '../models/SettingsModel'
import { ProductAppendInterface } from '../models/ProductModel'

export async function getOrders(
  type: OrderType | undefined = undefined
): Promise<OrderInterface[]> {
  const result = await api<OrderResponseInterface[]>({
    method: 'GET',
    url: '/orders',
    params: { type },
  })

  const data = unwrapApiResult(result)

  return (Array.isArray(data?.data) ? data.data : []).map(parseOrderModel)
}

export async function getOrderSettings(): Promise<SettingsInterface> {
  const result = await api<SettingsResponseInterface>({
    method: 'GET',
    url: '/settings',
  })

  const data = unwrapApiResult(result)

  return parseSettingsModel(data)
}

export async function getOrderSummary(): Promise<OrderTypeSummaryItem[]> {
  const result = await api<OrderTypeSummaryResponse>({
    method: 'GET',
    url: '/orders/stats',
  })

  const data = unwrapApiResult(result)

  return parseOrderTypeSummary(data)
}

export async function getOrder(orderId: string): Promise<OrderInterface | null> {
  const { data } = await api<OrderResponseInterface[]>({
    method: 'GET',
    url: '/orders',
  })

  if (!data?.data || !data.data.length) {
    return null
  }

  const foundOrder = data.data.filter((order) => order.id === orderId)

  if (!foundOrder.length) {
    return null
  }

  return parseOrderModel(foundOrder[0])
}

export async function getOrderItems(orderId: string): Promise<ItemInterface[]> {
  const result = await api<OrderResponseInterface[]>({
    method: 'GET',
    url: `/orders/${orderId}/items`,
  })

  const data = unwrapApiResult(result)

  return (Array.isArray(data?.data) ? data.data : []).map(parseItemModel)
}

export async function updateOrderStatus(
  orderId: string,
  newStatus: string
): Promise<ItemInterface[]> {
  const result = await api<OrderResponseInterface[]>({
    method: 'PUT',
    url: `/orders/${orderId}/status`,
    data: { status: newStatus },
  })

  const data = unwrapApiResult(result)

  return (Array.isArray(data?.data) ? data.data : []).map(parseItemModel)
}

export async function updateOrderComment(orderId: string, info: string): Promise<ItemInterface[]> {
  const result = await api<OrderResponseInterface[]>({
    method: 'PUT',
    url: `/orders/${orderId}/info`,
    data: { info },
  })

  const data = unwrapApiResult(result)

  return (Array.isArray(data?.data) ? data.data : []).map(parseItemModel)
}

export async function updateOrderItemQuantity(
  itemId: string,
  quantity: number,
  info: string | undefined = undefined
): Promise<ItemInterface> {
  const result = await apiWithRetry(
    {
      method: 'PUT',
      url: `/items/${itemId}`,
      data: {
        quantity_collected: quantity,
        info,
      },
    },
    { retries: 2, baseDelayMs: 400, maxDelayMs: 2500 }
  )

  const data = unwrapApiResult(result)

  return parseItemModel(data?.data)
}

export async function validateOrder(orderId: string): Promise<OrderValidationResponse> {
  const result = await apiWithRetry(
    {
      method: 'GET',
      url: `/orders/${orderId}/validate`,
    },
    { retries: 2, baseDelayMs: 400, maxDelayMs: 2500 }
  )

  const data = unwrapApiResult(result)

  return parseOrderValidationModel(data)
}

export async function completeOrder(
  orderId: string,
  completeAsReservation = false,
  completeAsOffer = false
): Promise<void> {
  let orderFinishType = 'finish'
  if (completeAsReservation) {
    orderFinishType = 'reservation'
  }
  if (completeAsOffer) {
    orderFinishType = 'offer'
  }
  const result = await apiWithRetry(
    {
      method: completeAsReservation || completeAsOffer ? 'POST' : 'PUT',
      url: `/orders/${orderId}/${orderFinishType}`,
    },
    { retries: 2, baseDelayMs: 400, maxDelayMs: 2500 }
  )

  unwrapApiResult(result)
}

export async function appendItemsToOrder(
  orderId: string,
  items: ProductAppendInterface[]
): Promise<void> {
  const result = await apiWithRetry(
    {
      method: 'POST',
      url: `/orders/${orderId}/items`,
      data: { items },
    },
    { retries: 2, baseDelayMs: 400, maxDelayMs: 2500 }
  )

  unwrapApiResult(result)
}
